"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { PlusCircle } from "lucide-react"
import type { Client } from "@/utils/types"

interface AddClientDialogProps {
    onClientAdded: (client: Client) => void
    clientService: any
}

export default function AddClientDialog({ onClientAdded, clientService }: AddClientDialogProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [newClient, setNewClient] = useState({
        name: "",
        address: "",
        email: "",
        phone: "",
    })
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setNewClient((prev) => ({ ...prev, [name]: value }))
    }

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            // Call the service to add the client
            const response = await clientService.addClient(newClient)

            // Create a properly structured client object
            const addedClient = {
                id: response?.id || Date.now(), // Use response id or fallback to timestamp
                name: newClient.name,
                address: newClient.address,
                email: newClient.email,
                phone: newClient.phone,
                // Include any other fields that might be needed
            }

            // Pass the properly structured client back to parent
            onClientAdded(addedClient)

            // Reset form and close dialog
            setNewClient({ name: "", address: "", email: "", phone: "" })
            setIsOpen(false)
        } catch (error) {
            console.error("Eroare la adaugare client:", error)
            alert("Eroare la adaugare client")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="icon">
                    <PlusCircle className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Adaugă client nou</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Nume</Label>
                        <Input id="name" name="name" value={newClient.name} onChange={handleInputChange} required />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="address">Adresă</Label>
                        <Input id="address" name="address" value={newClient.address} onChange={handleInputChange} required />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" name="email" type="email" value={newClient.email} onChange={handleInputChange}  />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phone">Telefon</Label>
                        <Input id="phone" name="phone" value={newClient.phone} onChange={handleInputChange} required />
                    </div>

                    <Button type="submit" className="w-full bg-red" disabled={isSubmitting}>
                        {isSubmitting ? "Adding..." : "Adauga Client"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    )
}

